// Núcleo de i18n PARTILHADO (lean + consola HQ): idiomas, variantes BCP 47 e
// precedência de deteção. Puro, sem dependências — cada app traz o seu carregador.

// Configuração i18n — fonte única dos idiomas suportados (BCP 47).
// Idioma ≠ país ≠ moeda ≠ fuso: o locale só decide TEXTO e formato de
// números/datas. Moeda e fuso são preferências separadas (nunca derivadas daqui).

export const LOCALES = ['pt', 'en', 'fr', 'es'] as const;
export type Locale = (typeof LOCALES)[number];

/** Idioma por omissão (configurável sem tocar no resto do código). */
export const DEFAULT_LOCALE: Locale = 'pt';

/** Usar o idioma do navegador quando não há escolha explícita? Por omissão NÃO: o produto abre em `DEFAULT_LOCALE`. */
export const DETECT_BROWSER_LANGUAGE = false;

/** Variante regional inicial de cada idioma — usada por `Intl` e pelo `lang` do HTML. */
export const REGION_TAG: Record<Locale, string> = {
  pt: 'pt-PT',
  en: 'en-US',
  fr: 'fr-FR',
  es: 'es-ES',
};

/** Locale `Intl` dos formatos numéricos/datas. pt mantém pt-AO (mercado do produto, agrupa 4 dígitos); o `lang` do HTML continua pt-PT. */
export const FORMAT_TAG: Record<Locale, string> = { pt: 'pt-AO', en: 'en-US', fr: 'fr-FR', es: 'es-ES' };

/** Nomes nativos (nunca traduzidos: quem não entende o idioma atual tem de se achar). */
export const NATIVE_NAME: Record<Locale, string> = {
  pt: 'Português',
  en: 'English',
  fr: 'Français',
  es: 'Español',
};

/** Namespaces por domínio. `common` carrega sempre; os restantes sob demanda. */
export const NAMESPACES = ['common'] as const;
export type Namespace = (typeof NAMESPACES)[number];

export const isLocale = (v: unknown): v is Locale =>
  typeof v === 'string' && (LOCALES as readonly string[]).includes(v);

/** 'fr-CA' / 'pt_BR' / 'EN' → idioma suportado, ou null. */
export function normalizeLocale(raw: unknown): Locale | null {
  if (typeof raw !== 'string') return null;
  const base = raw.trim().toLowerCase().replace('_', '-').split('-')[0];
  return isLocale(base) ? base : null;
}


const STORAGE_KEY = 'marc:locale';

export function readStoredLocale(): Locale | null {
  try { return normalizeLocale(localStorage.getItem(STORAGE_KEY)); } catch { return null; }
}
export function storeLocale(l: Locale): void {
  try { localStorage.setItem(STORAGE_KEY, l); } catch { /* storage bloqueado: segue só em memória */ }
}

/**
 * Precedência determinística (da mais forte para a mais fraca):
 *   1. perfil autenticado (`preferences.locale`) — viaja entre dispositivos
 *   2. escolha local neste dispositivo (localStorage)
 *   3. `?lng=` no URL (ligações partilháveis / campanhas)
 *   4. idioma do navegador (só se DETECT_BROWSER_LANGUAGE)
 *   5. DEFAULT_LOCALE
 * Uma escolha explícita no seletor grava em (1) e (2), por isso ganha sempre.
 */
export function resolveLocale(opts: { profile?: unknown } = {}): Locale {
  const profile = normalizeLocale(opts.profile);
  if (profile) return profile;
  const stored = readStoredLocale();
  if (stored) return stored;
  try {
    const fromUrl = normalizeLocale(new URLSearchParams(window.location.search).get('lng'));
    if (fromUrl) return fromUrl;
  } catch { /* sem window (testes) */ }
  if (DETECT_BROWSER_LANGUAGE) for (const l of (typeof navigator !== 'undefined' ? navigator.languages ?? [navigator.language] : [])) {
    const n = normalizeLocale(l);
    if (n) return n;
  }
  return DEFAULT_LOCALE;
}
